/**
 * Install instructions modal: shown when WebSocket connection fails.
 * Shows platform-specific commands via tabs (Mac / Windows).
 */
import React, { useState } from 'react';
import { Alert, Box, Button, CopyToClipboard, Link, Modal, SpaceBetween, Tabs } from '@amzn/awsui-components-core';
import styles from '../styles.module.scss';
import { WEBSOCKET_URL, INSTALL_COMMAND, INSTALL_COMMAND_MAC_LOCAL } from '../config';

interface InstallInstructionsModalProps {
  visible: boolean;
  onDismiss: () => void;
}

const CommandBlock: React.FC<{ command: string }> = ({ command }) => (
  <div className={styles.codeBlockWrapper}>
    <code className={styles.codeBlock} style={{ fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
      {command}
    </code>
    <CopyToClipboard
      copyButtonAriaLabel="Copy command"
      copyErrorText="Failed to copy"
      copySuccessText="Command copied"
      textToCopy={command}
      variant="icon"
    />
  </div>
);

export const InstallInstructionsModal: React.FC<InstallInstructionsModalProps> = ({ visible, onDismiss }) => {
  const [activeTab, setActiveTab] = useState('mac');

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="⚠️ Upgrade Medic Server Not Running"
      size="medium"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box color="text-status-error">
          Unable to connect to the Upgrade Medic server at <code>{WEBSOCKET_URL}</code>
        </Box>

        <Box variant="h4">Installation Instructions</Box>

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              id: 'mac',
              label: 'Mac',
              content: (
                <SpaceBetween size="m">
                  <Box fontWeight="bold">Run Upgrade Medic on Cloud Desktop from Mac Terminal (Recommended)</Box>
                  <Box fontSize="body-s" color="text-body-secondary">
                    Run this command in your local Mac terminal.
                  </Box>
                  <CommandBlock command={INSTALL_COMMAND} />

                  <Box fontWeight="bold" padding={{ top: 's' }}>
                    Run Upgrade Medic Locally on Mac
                  </Box>
                  <Box fontSize="body-s" color="text-body-secondary">
                    If you prefer to run directly on your Mac without Cloud Desktop, use the <code>--local</code> flag.
                  </Box>
                  <CommandBlock command={INSTALL_COMMAND_MAC_LOCAL} />
                </SpaceBetween>
              )
            },
            {
              id: 'windows',
              label: 'Windows',
              content: (
                <SpaceBetween size="m">
                  <Alert type="info">Windows users must run this from a Cloud Desktop. Local Windows execution is not supported.</Alert>
                  <Box fontWeight="bold">Run Upgrade Medic on Cloud Desktop</Box>
                  <Box fontSize="body-s" color="text-body-secondary">
                    SSH into your Cloud Desktop and run this command in the terminal.
                  </Box>
                  <CommandBlock command={INSTALL_COMMAND} />
                </SpaceBetween>
              )
            }
          ]}
        />

        <Box color="text-body-secondary" fontSize="body-s">
          This will authenticate via Midway, install Rodar, and download the Upgrade Medic server package. The server runs locally on your machine to
          execute version updates.
        </Box>

        <Box fontSize="body-s">
          Having issues? See the{' '}
          <Link href="https://w.amazon.com/bin/view/Upgrade-Medic-User-Guide/#HTroubleshootingSteps" external>
            Troubleshooting Guide
          </Link>{' '}
          or{' '}
          <Link href="https://t.corp.amazon.com/create/templates/b74ae449-480e-4ce3-9ddc-d89af3cf5dfe" external>
            Cut a Support Ticket
          </Link>
        </Box>
      </SpaceBetween>
    </Modal>
  );
};
